import { Controller, useFormContext, useWatch } from "react-hook-form";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ChipRadioGroup } from "@/components/ui/chip-radio";
import { Field } from "@/components/ui/field";
import { Textarea } from "@/components/ui/textarea";
import { AddressField } from "@/components/form/AddressField";
import { AddressPickHint } from "@/components/form/AddressPickHint";
import { AddressConfirmedHint } from "@/components/form/AddressConfirmedHint";
import { TimeField } from "@/components/form/TimeField";
import { formatTimeForDisplay, fullAddressFromPlace, needsAmPm } from "@/lib/format";
import { WizardCard } from "./WizardCard";
import type { TripFormValues } from "./schema";
import type { WizardStepStatus } from "./WizardCard";

interface ReturnTripSectionProps {
  index: number;
  status: WizardStepStatus;
  onContinue: () => void;
  onEdit: () => void;
}

export function ReturnTripSection({
  index,
  status,
  onContinue,
  onEdit,
}: ReturnTripSectionProps) {
  const {
    control,
    register,
    setValue,
    formState: { errors },
    trigger,
  } = useFormContext<TripFormValues>();

  const mode = useWatch({ control, name: "returnTimeMode" });
  const returnPickupTime = useWatch({ control, name: "returnPickupTime" }) ?? "";
  const returnToOriginal = useWatch({ control, name: "returnToOriginalPickup" });
  const returnAddress =
    useWatch({ control, name: "returnCustomDestinationAddress" }) ?? "";
  const returnPlace = useWatch({ control, name: "returnDestinationPlace" });

  // "Location confirmed" when we have coordinates, an amber pick-from-dropdown
  // nudge when we don't (the server backfills as a safety net, so it never
  // blocks the booking). The two states are mutually exclusive.
  const hasReturnCoords = Boolean(
    returnPlace &&
      returnPlace.lat != null &&
      returnPlace.lon != null &&
      Number.isFinite(returnPlace.lat) &&
      Number.isFinite(returnPlace.lon),
  );
  const hasReturnAddress = returnAddress.trim().length > 0;

  async function handleContinue() {
    const fields: Array<keyof TripFormValues> = [];
    if (mode === "scheduled") fields.push("returnPickupTime");
    if (!returnToOriginal) {
      fields.push("returnCustomDestinationAddress");
    }
    if (fields.length > 0) {
      const ok = await trigger(fields);
      if (!ok) return;
    }
    onContinue();
  }

  if (status === "complete") {
    const time =
      mode === "will_call"
        ? "Will Call"
        : returnPickupTime
          ? needsAmPm(returnPickupTime)
            ? returnPickupTime
            : formatTimeForDisplay(returnPickupTime)
          : "—";
    const dest = returnToOriginal
      ? "→ original pickup"
      : returnAddress
        ? `→ ${returnAddress}`
        : "→ ?";
    return (
      <WizardCard
        index={index}
        title="Return trip"
        status="complete"
        summary={`${time} ${dest}`}
        onEdit={onEdit}
      />
    );
  }

  return (
    <WizardCard
      index={index}
      title="Return trip"
      description="Return pick-up starts from the drop-off location."
      status={status}
    >
      <Controller
        control={control}
        name="returnTimeMode"
        render={({ field }) => (
          <Field label="Return pickup" required>
            <ChipRadioGroup
              ariaLabel="Return pickup mode"
              value={field.value}
              onValueChange={field.onChange}
              options={[
                { value: "scheduled", label: "Scheduled Time" },
                { value: "will_call", label: "Will Call" },
              ]}
            />
          </Field>
        )}
      />

      {mode === "scheduled" ? (
        <Field
          label="Return pickup time"
          required
          htmlFor="returnPickupTime"
          error={errors.returnPickupTime?.message}
          hint="Must be after the initial pickup/appointment time."
        >
          <Controller
            control={control}
            name="returnPickupTime"
            render={({ field, fieldState }) => (
              <TimeField
                id="returnPickupTime"
                value={field.value ?? ""}
                onChange={field.onChange}
                onBlur={field.onBlur}
                invalid={Boolean(fieldState.error)}
              />
            )}
          />
        </Field>
      ) : (
        <p className="rounded-xl border border-dashed border-primary/40 bg-primary/10 px-3 py-2 text-sm font-semibold text-primary">
          Patient will call when ready for return pickup.
        </p>
      )}

      <Controller
        control={control}
        name="returnToOriginalPickup"
        render={({ field }) => (
          <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-foreground/90">
            <Checkbox
              checked={field.value}
              onCheckedChange={(c) => field.onChange(c === true)}
            />
            Return to original pick-up location
          </label>
        )}
      />

      {!returnToOriginal ? (
        <div className="flex flex-col gap-3">
          <Field
            label="Return to (different address)"
            required
            htmlFor="returnCustomDestinationAddress"
            error={errors.returnCustomDestinationAddress?.message}
          >
            <Controller
              control={control}
              name="returnCustomDestinationAddress"
              render={({ field }) => (
                <AddressField
                  value={field.value ?? ""}
                  onChange={field.onChange}
                  onPlace={(place, cityZip) => {
                    // Full canonical address — see PickupSection / fullAddressFromPlace.
                    field.onChange(fullAddressFromPlace(place, field.value ?? ""));
                    setValue("returnDestinationPlace", place, { shouldDirty: true });
                    if (cityZip)
                      setValue("returnCustomDestinationCityZip", cityZip, {
                        shouldDirty: true,
                      });
                  }}
                  onClearPlace={() => {
                    setValue("returnDestinationPlace", null, { shouldDirty: true });
                    setValue("returnCustomDestinationCityZip", "", { shouldDirty: true });
                  }}
                  placeholder="Enter different return destination"
                  maxLength={200}
                  invalid={Boolean(errors.returnCustomDestinationAddress)}
                />
              )}
            />
          </Field>
          {hasReturnAddress ? (
            hasReturnCoords ? <AddressConfirmedHint /> : <AddressPickHint />
          ) : null}
        </div>
      ) : null}

      <Field label="Return leg notes (optional)" htmlFor="returnLegNotes">
        <Textarea
          id="returnLegNotes"
          rows={2}
          placeholder="Any notes for the return leg…"
          maxLength={1000}
          {...register("returnLegNotes")}
        />
      </Field>

      <div className="flex justify-end pt-1">
        <Button type="button" onClick={handleContinue} className="min-w-[10rem]">
          Continue
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </WizardCard>
  );
}
